import { Theme } from '../styles/styles';
import Decimal from 'decimal.js';
import { Point, Tooltip, TooltipFormatterCallbackFunction } from 'highcharts';
import { DateTime, DateTimeFormatOptions, LocaleOptions } from 'luxon';
import { fontDisplay, fontText } from 'common-react';
import { formatNumber } from './formatters';
import { ColorsPaletteLight } from '../theme/constants';

const formatTooltipValueHtml = (formattedValue: string) => {
  const match = /^(.+?)\s*(PLN|%)$/.exec(formattedValue);
  if (match) {
    const suffix = match[2] === 'PLN' ? ' ' + match[2] : match[2];
    return `<span style="font-weight: bold;">${match[1]}</span>${suffix}`;
  }
  return `<span style="font-weight: bold;">${formattedValue}</span>`;
};
export interface TooltipFormatterOptions {
  locale: string;
  theme: Theme;
  isDarkTheme?: boolean;
  formatValue?: (value: number, point: Point) => string;
  formatNumber?: (value: Decimal, decimalScale?: number, fixedDecimalScale?: boolean, addSpacingBeforeLastThreeIntDigits?: boolean, showMinusSignForNegatives?: boolean) => string;
  isMobile: boolean;
}

export const createTooltipFormatter = (options: TooltipFormatterOptions): TooltipFormatterCallbackFunction => {
  const {
    isMobile,
    locale,
    theme,
    formatNumber: formatNumberFn = formatNumber,
    formatValue = (value: number) => `${formatNumberFn(new Decimal(value))} PLN`
  } = options;

  return function(this: Point & {points: Point[]}, _tooltip: Tooltip) {
    const date: string = DateTime.fromJSDate(new Date(this.x))
        .setLocale(locale)
        .toLocaleString(DateTime.DATE_MED as DateTimeFormatOptions & LocaleOptions);
    const maxWidthOnDesktop = isMobile ? '' : 'width: max-content;';
    const sortedLabels = this.points?.slice().sort((a, b) => (b.y ?? 0) - (a.y ?? 0));
    const labels = sortedLabels
      ?.map(point => {
        const value = formatValue(point.y ?? 0, point);
        const noWrapOnDesktopOnly = `${isMobile ? '' : 'white-space: nowrap;'}`;
        const labelFontSize = isMobile ? theme.fontSize.xs : theme.fontSize.s;

        return `
        <div style="display: flex; align-items: center; justify-content: space-between; ${noWrapOnDesktopOnly}">
          <div style="
            display: flex;
            align-items: center;
          ">
          <div style="margin-right: 8px; width: 4px; height: 20px; background-color: ${String(point.color)}; flex-shrink: 0;"></div>
          <div style="font: ${fontText('normal', labelFontSize, 20)}; margin-right: 24px; color: ${theme.color.greys[80]}; ${noWrapOnDesktopOnly}">${point.series.name}</div></div>
          <div style="font: ${fontText('normal', labelFontSize, 20)}; color: ${theme.color.greys[80]}; position: relative; flex-shrink: 0; ${noWrapOnDesktopOnly}">${formatTooltipValueHtml(value)}</div>
        </div>
        `;
    });

    return `
      <div style="display: flex; flex-direction: column; gap: 8px; padding: 0; ${maxWidthOnDesktop}">
        <div style="
          font: ${fontDisplay('normal', theme.fontSize.s, 20)};
          color: ${theme.color.greys[80]};
        ">
          ${date}</div>
        ${labels?.join('') || ''}
      </div>
    `;
  } as TooltipFormatterCallbackFunction;
};

export interface TooltipOptionsConfig {
  isMobile: boolean;
  locale: string;
  theme: Theme;
  isDarkTheme?: boolean;
  formatNumber?: (value: Decimal, decimalScale?: number, fixedDecimalScale?: boolean, addSpacingBeforeLastThreeIntDigits?: boolean, showMinusSignForNegatives?: boolean) => string;
  formatValue?: (value: number, point: Point) => string;
  split?: boolean;
  shared?: boolean;
}

export const createTooltipOptions = (config: TooltipOptionsConfig) => {
  const {
    isMobile,
    locale,
    theme,
    isDarkTheme = false,
    formatNumber: formatNumberFn = formatNumber,
    formatValue = (value: number) => `${formatNumberFn(new Decimal(value))} PLN`,
    split = false,
    shared = true
  } = config;
  return {
    split,
    shared,
    useHTML: true,
    formatter: createTooltipFormatter({
      isMobile,
      locale,
      theme,
      isDarkTheme,
      formatNumber: formatNumberFn,
      formatValue
    }),
    backgroundColor: ColorsPaletteLight.greys[0],
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.color.greys[15],
    padding: 0,
    shape: "square" as const,
    shadow: false,
    style: {
      fontFamily: theme.fontFamily.ingMeWebRegular,
      color: theme.color.greys[80],
      whiteSpace: isMobile ? 'normal' : 'nowrap',
    }
  };
};
